import { useEffect, useMemo } from "react";
import { useGetAIModels, useChatInfo } from "../api/queryHooks";
import useModelStore from "../store/useModelStore";
import useModelPreferences from "./useModelPreferences";
import useHeaderSelectedAIModel from "../store/useHeaderSelectedAIModel";

/**
 * Derives the correct selected model for a given chat declaratively.
 *
 * Priority order (highest to lowest):
 *   0. caller-provided initialModelId (e.g. model that generated an answer being edited)
 *   1. user's explicit override for this chat_id (stored in useModelStore)
 *   2. model persisted on the chat document in the backend
 *   3. user's starred default model from preferences
 *   4. first available enabled model
 *
 * Returns { resolvedModel, isLoading, options }
 *   - resolvedModel: the model object, or null if data is still loading
 *   - isLoading: true while any required data source is still pending
 *   - options: the filtered enabled model list (passed through for the dropdown)
 */
const useResolvedModel = ({ chat_id, space, initialModelId = null } = {}) => {
  const { data: allModels, isLoading: modelsLoading } = useGetAIModels();
  const { defaultModel, getEnabledModels, isLoading: preferencesLoading } = useModelPreferences();
  const { data: chatData, isLoading: chatLoading } = useChatInfo(chat_id, space, {
    enabled: !!chat_id,
  });

  const userOverrideModelId = useModelStore((state) =>
    chat_id ? state.selectedModels[chat_id] : null
  );

  const persistedChatModelId = chatData?.persisted_model_id ?? null;

  const options = useMemo(() => getEnabledModels(allModels), [allModels, getEnabledModels]);

  const isLoading = modelsLoading || preferencesLoading || (!!chat_id && chatLoading);

  const collaboratorModelUpdate = useHeaderSelectedAIModel((state) => state.collaboratorModelUpdate);
  const clearCollaboratorModelUpdate = useHeaderSelectedAIModel((state) => state.clearCollaboratorModelUpdate);

  // When a collaborator changes the model, record it as a per-chat override so
  // resolvedModel picks it up at Priority 1 on the next render.
  useEffect(() => {
    if (!collaboratorModelUpdate || !options || !chat_id) return;
    const found = options.find((m) => m.id === collaboratorModelUpdate.model_id);
    if (found) {
      useModelStore.getState().setSelectedModel(chat_id, found.id);
    }
    clearCollaboratorModelUpdate();
  }, [collaboratorModelUpdate, options, chat_id, clearCollaboratorModelUpdate]);

  const resolvedModel = useMemo(() => {
    if (!options || options.length === 0) return null;

    const find = (id) => options.find((m) => m?.id === id) ?? null;

    // Priority 0 — caller-provided model id
    if (initialModelId) {
      const found = find(initialModelId);
      if (found) return found;
    }

    // Priority 1 — explicit user selection this session (per chat)
    if (userOverrideModelId) {
      const found = find(userOverrideModelId);
      if (found) return found;
    }

    // Priority 2 — model persisted on the chat document
    if (persistedChatModelId) {
      const found = find(persistedChatModelId);
      if (found) return found;
    }

    // Preferences must be settled before we use defaultModel to avoid
    // flickering from options[0] → defaultModel as prefs load.
    if (preferencesLoading) return null;

    // Priority 3 — user's starred default
    if (defaultModel) {
      const found = find(defaultModel);
      if (found) return found;
    }

    // Priority 3.5 — org-level default (DEFAULT_TEXT_MODEL set by admin in Settings → Models → Defaults)
    // is_default is tagged on the model object by getAIModels() via /model/defaults/get
    const orgDefault = options.find((m) => m.is_default);
    if (orgDefault) return orgDefault;

    // Priority 4 — first available enabled model
    return options[0] ?? null;
  }, [options, initialModelId, userOverrideModelId, persistedChatModelId, defaultModel, preferencesLoading]);

  return { resolvedModel, isLoading, options, chatExists: !!chatData?.uid };
};

export default useResolvedModel;
